/**
 * Local movement-mode preference: 'free' (mouse + keyboard exploration) or
 * 'follow' (auto-join the presenter's Follow Me tour whenever one is active).
 *
 * The user picks a mode in the welcome modal (levelManager.js) and can flip it
 * later via the on-screen toggle (multiplayerUI.js). multiplayer.js consumes
 * the mode to decide whether incoming follow_start events / the room's
 * presenterState property should steer the local camera, and flips the mode
 * back to 'free' when the user takes back control by moving.
 *
 * Pure local state - nothing here touches the network.
 */

/** @typedef {'free' | 'follow'} MovementMode */

/** @type {MovementMode} */
let mode = 'free';

/** @type {Set<(mode: MovementMode) => void>} */
const listeners = new Set();

/** @returns {MovementMode} */
export function getMovementMode() {
  return mode;
}

export function isFollowModeOn() {
  return mode === 'follow';
}

/**
 * Change the mode and notify listeners (no-op if unchanged).
 * @param {MovementMode} next
 */
export function setMovementMode(next) {
  if (next !== 'free' && next !== 'follow') return;
  if (next === mode) return;
  mode = next;
  listeners.forEach((fn) => {
    try {
      fn(mode);
    } catch (e) {
      console.warn('movementMode listener failed:', e);
    }
  });
}

/**
 * Subscribe to mode changes.
 * @param {(mode: MovementMode) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onMovementModeChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
